import React, { useRef } from "react";
import { Upload, X } from "lucide-react";
import { APP_ICONS, APP_COLORS, AppIcon } from "@/components/apps/AppIcon";
import { toast } from "sonner";

const LABEL = "font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4] mb-1 block";
const TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif", "image/svg+xml", "image/x-icon", "image/vnd.microsoft.icon"];

// value: { kind, name, color }, file: pending upload File, preview: object URL
export function AppIconPicker({ appId, value, onChange, file, onFile, preview }) {
  const ref = useRef(null);
  const pick = (f) => {
    if (!f) return;
    if (!TYPES.includes(f.type)) { toast.error("Please choose a PNG, JPG, WEBP, GIF, SVG or ICO image"); return; }
    if (f.size > 2 * 1024 * 1024) { toast.error("Icon must be 2 MB or smaller"); return; }
    onFile(f);
  };
  const color = value.color || "violet";
  return (
    <div data-testid="app-icon-picker">
      <label className={LABEL}>Card icon</label>
      <div className="flex items-center gap-3 mb-3">
        <AppIcon appId={appId} icon={value} preview={file ? preview : null} size="lg" testId="app-icon-preview" />
        <input ref={ref} type="file" hidden accept={TYPES.join(",")} data-testid="app-icon-file" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
        <button type="button" data-testid="app-icon-upload" onClick={() => ref.current?.click()} className="rounded-full border border-[#EEEFEF] px-3 py-1.5 text-xs text-[#06060C] hover:bg-[#F6F6FD] flex items-center gap-1.5"><Upload className="h-3.5 w-3.5" /> Upload favicon</button>
        {(file || value.kind !== "default") && <button type="button" data-testid="app-icon-reset" onClick={() => { onFile(null); onChange({ kind: "default", name: "", color: "" }); }} className="text-xs text-[#9FA1A4] hover:text-rose-600 flex items-center gap-1"><X className="h-3.5 w-3.5" /> Use default</button>}
      </div>
      <div className="grid grid-cols-10 gap-1.5 mb-2.5" data-testid="app-icon-grid">
        {Object.entries(APP_ICONS).map(([k, I]) => {
          const on = !file && value.kind === "preset" && value.name === k;
          return <button type="button" key={k} title={k} data-testid={`app-icon-opt-${k}`} onClick={() => { onFile(null); onChange({ kind: "preset", name: k, color }); }}
            className={`h-8 w-8 rounded-lg flex items-center justify-center border transition-colors ${on ? "border-[#723FFF] bg-[#EDE7FF] text-[#723FFF]" : "border-[#EEEFEF] text-[#595D62] hover:bg-[#F6F6FD]"}`}><I className="h-4 w-4" /></button>;
        })}
      </div>
      <div className="flex items-center gap-2" data-testid="app-icon-colors">
        {Object.entries(APP_COLORS).map(([k, [bg, fg]]) => (
          <button type="button" key={k} title={k} data-testid={`app-icon-color-${k}`} onClick={() => { onFile(null); onChange({ kind: "preset", name: value.kind === "preset" ? value.name : "sparkles", color: k }); }}
            className={`h-6 w-6 rounded-full border-2 transition-transform hover:scale-110 ${!file && value.kind === "preset" && color === k ? "border-[#06060C]" : "border-white"}`} style={{ background: fg, boxShadow: `0 0 0 1px ${bg}` }} />
        ))}
      </div>
    </div>
  );
}
